/**
 * JSON formatting utilities for handling response messages and other JSON data
 */

/**
 * Formats a response message by attempting to parse and prettify JSON content
 * @param {string} responseMessage - The raw response message to format
 * @returns {string} Formatted message or original text if not JSON
 */
export function formatResponseMessage (responseMessage) {
	if (!responseMessage) {
		return 'No detailed response message available'
	}

	// Ensure we're working with a string
	if (typeof responseMessage !== 'string') {
		responseMessage = String(responseMessage)
	}

	// Try to parse as JSON and format it
	try {
		const parsed = JSON.parse(responseMessage)
		return JSON.stringify(parsed, null, 2)
	} catch (error) {
		// If it's not valid JSON, check if it looks like JSON but might have formatting issues
		return attemptJsonCleaning(responseMessage)
	}
}

/**
 * Attempts to clean and parse JSON-like strings that may have formatting issues
 * @param {string} responseMessage - The response message to attempt cleaning
 * @returns {string} Cleaned and formatted JSON or original message
 */
function attemptJsonCleaning (responseMessage) {
	const trimmed = responseMessage.trim()

	// Check if it looks like JSON structure
	if (isJsonLike(trimmed)) {
		try {
			// Try cleaning approach: normalize whitespace
			const cleaned = trimmed.replace(/[\r\n\t]/g, ' ').replace(/\s+/g, ' ')
			const parsed = JSON.parse(cleaned)
			return JSON.stringify(parsed, null, 2)
		} catch (secondError) {
			// Try additional cleaning techniques
			return attemptAdvancedJsonCleaning(trimmed) || responseMessage
		}
	}

	// Not JSON-like, return as plain text
	return responseMessage
}

/**
 * Checks if a string looks like it could be JSON
 * @param {string} text - The text to check
 * @returns {boolean} True if the text appears to be JSON-like
 */
function isJsonLike (text) {
	return (text.startsWith('{') && text.endsWith('}')) ||
  (text.startsWith('[') && text.endsWith(']'))
}

/**
 * Attempts advanced JSON cleaning techniques for malformed JSON
 * @param {string} text - The text to attempt cleaning
 * @returns {string|null} Cleaned JSON string or null if cleaning failed
 */
function attemptAdvancedJsonCleaning (text) {
	try {
		// Remove common JSON formatting issues
		const cleaned = text
			// Fix trailing commas
			.replace(/,(\s*[}\]])/g, '$1')
			// Fix unquoted keys (basic cases)
			.replace(/([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/g, '$1"$2":')
			// Fix single quotes to double quotes
			.replace(/'/g, '"')
			// Remove comments (basic // and /* */ patterns)
			.replace(/\/\/.*$/gm, '')
			.replace(/\/\*[\s\S]*?\*\//g, '')
			// Normalize whitespace again
			.replace(/\s+/g, ' ')
			.trim()

		const parsed = JSON.parse(cleaned)
		return JSON.stringify(parsed, null, 2)
	} catch (error) {
		// Advanced cleaning failed
		return null
	}
}

/**
 * Validates if a string is valid JSON
 * @param {string} text - The text to validate
 * @returns {boolean} True if the text is valid JSON
 */
export function isValidJson (text) {
	if (!text || typeof text !== 'string') {
		return false
	}

	try {
		JSON.parse(text)
		return true
	} catch (error) {
		return false
	}
}

/**
 * Safely parses JSON with error handling
 * @param {string} text - The JSON text to parse
 * @param {*} defaultValue - Default value to return if parsing fails
 * @returns {*} Parsed JSON object or default value
 */
export function safeJsonParse (text, defaultValue = null) {
	try {
		return JSON.parse(text)
	} catch (error) {
		console.warn('Failed to parse JSON:', error.message)
		return defaultValue
	}
}

/**
 * Safely stringifies an object to JSON with error handling
 * @param {*} obj - The object to stringify
 * @param {number} spaces - Number of spaces for indentation (default: 2)
 * @param {string} fallback - Fallback string if stringification fails
 * @returns {string} JSON string or fallback
 */
export function safeJsonStringify (obj, spaces = 2, fallback = 'Unable to format object') {
	try {
		return JSON.stringify(obj, null, spaces)
	} catch (error) {
		console.warn('Failed to stringify object:', error.message)
		return fallback
	}
}

/**
 * Minifies JSON by removing unnecessary whitespace
 * @param {string} jsonString - The JSON string to minify
 * @returns {string} Minified JSON string or original if parsing fails
 */
export function minifyJson (jsonString) {
	try {
		const parsed = JSON.parse(jsonString)
		return JSON.stringify(parsed)
	} catch (error) {
		console.warn('Failed to minify JSON:', error.message)
		return jsonString
	}
}
